import { BaseViewStore, BaasicDropdownStore } from "core/stores";
import { PickTicketHeaderService } from "application/pick-ticket/services";
import { FacilityOrderService, FacilityOrderTemplateService, FacilityOrderStatusService, FacilityOrderProductService } from "application/facility-order/services";
import { NikoPatientDetailService, NikoPatientTemplateItemService, NikoPatientTemplateService } from "application/niko-patient/services";
import { HistoryService } from "application/history/services";
import { ScheduleService } from 'application/schedule/services';
import { action, computed, observable, runInAction } from "mobx";
import { applicationContext, QueryUtility, getErrorMessage } from 'core/utils';
import _, { debounce } from 'lodash';
import { LookupService } from "common/services";
import { ModalParams } from 'core/models';

@applicationContext
class NikoFacilityOrderOrderSummaryPreviewViewStore extends BaseViewStore {
	@observable accessor facilityOrderTemplateItemsGroupedByItemCategory = [];
	@observable accessor facilityOrderTemplateItemsGroupedByItemCategoryEditMode = [];
	@observable accessor isStatusBarVisible = true;
	@observable accessor fetchingData = false;
	@observable accessor signalRFetch = false;
	@observable accessor isEditDisabled = false;
	@observable accessor shouldShowItemActionButtons = false;
	@observable accessor savingResource = false;
	@observable accessor rebuildTooltip = false;
	@observable accessor facilityOrder;
	@observable accessor anyDNS = false;
	@observable accessor showAllDNS = false;
	@observable accessor item = null;
	@observable accessor disabledSendOrder = false;
	@observable accessor isSendOrderChecking = false;
	@observable accessor disabledItemButtons = false;
	@observable accessor clinicalReviewStatus;
	@computed
	get isOrderSummaryStarted() {
		return this.facilityOrder && this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'order-summary') !== undefined
	}
	@computed
	get isFacilityOrderSent() {
		return this.facilityOrder && this.facilityOrder.sendOrder;
	}
	@observable accessor patientFilterValue = null;
	@observable accessor hasBackorderedItem = false;
	@observable accessor sendingAgoraPo = false;
	@observable accessor creatingNikoSOsAllFacilityOrders = false;
	@observable accessor creatingNikoInvoicesAllFacilityOrders = false;
	agoraPoErrorMessage = "There are no items to include on the Agora PO.";
	ssQtyInputRef = '';

	constructor(rootStore) {
        super(rootStore);

        this.rootStore = rootStore;
        this.isAdmin = this.rootStore.userStore.user.roles.includes("Administrators");
        this.isMasterEditor = this.rootStore.userStore.user.roles.includes("Master Editor");
        this.isPurchaser = this.rootStore.userStore.user.roles.includes("Purchasers");
        this.facilityOrderId = rootStore.routerStore.routerState.params.id;
        this.nikoPatientTemplateItemService = new NikoPatientTemplateItemService(rootStore.application.baasic.apiClient);
        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.nikoPatientDetailService = new NikoPatientDetailService(rootStore.application.baasic.apiClient);
        this.nikoPatientTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);
        this.scheduleService = new ScheduleService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);
        this.facilityOrderProductService = new FacilityOrderProductService(rootStore.application.baasic.apiClient);
        this.historyService = new HistoryService(rootStore.application.baasic.apiClient);

        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
			await this.fetchFacilityOrderTemplateItems(filter);
		}, {});

        this.fetch([this.initialize()]);

        this.setAnyDNS = debounce(() => {
			this.anyDNS = false;
		}, 500);

        this.localPatientStore = new BaasicDropdownStore({
			filterable: true,
			textField: "name",
			initFetch: false,
		}, {
			fetchFunc: async () => {
				const nikoPatientDetails = (await this.nikoPatientDetailService.find({ facilityOrderId: this.facilityOrderId, embed: "nikoPatient", rpp: 1000, sort: "nikoPatient|asc" })).data.item;
				const finalPatients = nikoPatientDetails.filter(item => {

					if (item.status !== "" && item.status !== "Active" && item.status !== "Hospital") {
						return false;
					}

					if (item.status === "Active") {
						return this.originalData && this.originalData.some(od =>
							od.facilityOrderTemplateItemOrderSummaryDetails &&
							od.facilityOrderTemplateItemOrderSummaryDetails.some(detail =>
								detail.patientId === item.id &&
								detail.activeStatus === "Active"
							)
						);
					}

					return true;
				});

				return finalPatients.map(item => {
					return {
						name: `${item.nikoPatient.lastName}${(!item.nikoPatient.firstName || item.nikoPatient.firstName === "" ? "" : ",")} ${item.nikoPatient.firstName}`,
						patientId: item.id,
						isNewPatient: item.isNew || false,
						tag: item.status === "Hospital" ? "Hospital" : undefined,
						disabled: item.status === "Hospital",
						icon: true,
					};
				});
			}
		});

        this.cutbackConfirmModal = new ModalParams({ onClose: null });
        this.cutbackConfirmModal.onCancel = () => { this.cutbackConfirmModal.close(); };
        this.cutbackConfirmModal.yesLabel = 'Yes';
        this.cutbackConfirmModal.noLabel = 'Cancel';
        this.cutbackConfirmModal.swapButtons = true;

        this.sendOrderConfirmModal = new ModalParams({ onClose: null });
        this.sendOrderConfirmModal.onConfirm = async () => { this.sendOrderConfirmModal.close(); await this.sendOrder(); };
        this.sendOrderConfirmModal.onCancel = () => { this.sendOrderConfirmModal.close() };
        this.sendOrderConfirmModal.yesLabel = 'Yes';
        this.sendOrderConfirmModal.noLabel = 'No';

        this.addTubeConfirmModal = new ModalParams({ onClose: null });
        this.addTubeConfirmModal.onConfirm = async () => { this.addTubeConfirmModal.close(); await this.checkForMonthlyTubesNow(); };
        this.addTubeConfirmModal.onCancel = () => { this.addTubeConfirmModal.close() };
        this.addTubeConfirmModal.yesLabel = 'MODAL.ADD_TUBES_NOW_LABEL';
        this.addTubeConfirmModal.noLabel = 'MODAL.CANCEL_LABEL';
        this.addTubeConfirmModal.swapButtons = true;

        this.addIvPolesConfirmModal = new ModalParams({ onClose: null });
        this.addIvPolesConfirmModal.onConfirm = async () => { this.addIvPolesConfirmModal.close(); await this.addIvPoles(); };
        this.addIvPolesConfirmModal.onCancel = () => { this.addIvPolesConfirmModal.close() };
        this.addIvPolesConfirmModal.yesLabel = 'Yes';
        this.addIvPolesConfirmModal.noLabel = 'Cancel';

        this.primaryRecipientErrorModal = new ModalParams({ onClose: null });
        this.primaryRecipientErrorModal.onCancel = () => { this.primaryRecipientErrorModal.close() };
        this.primaryRecipientErrorModal.facilityOrderId = this.facilityOrderId;
        this.primaryRecipientErrorModal.isNiko = true;

        this.facilityOrderNewFlowUSIntakeFindAndReplaceModal = new ModalParams({});
        this.facilityOrderNewFlowExtraSuppliesAddModal = new ModalParams({});
        this.facilityOrderNewFlowRoundingItemsModal = new ModalParams({
			onClose: () => {
				this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
					category.items.forEach(item => {
						if (item.showRoundOption && item.patientCount < 4) {
							item.includeItem = false;
						}
					});
				});
			}
		});

        const userRoles = this.rootStore.userStore.user.roles;
        this.isAdmin = userRoles.includes("Administrators");
    }

	@action.bound
	async initialize() {
		const lookupService = new LookupService(this.rootStore.application.baasic.apiClient);
		this.lookups = await lookupService.getLookups("ItemCategory,ImportHeaderStatus,FacilityScheduleType");
		this.enteralItemCategoryId = _.find(this.lookups.itemCategory, s => { return s.abrv === 'enteral'; }).id;
		await this.initializeFacilityOrder();
		await this.hasBackorderedItems();
		this.queryUtility.fetch();
		this.patientName = '';
		this.itemName = '';
		this.rootStore.titleStore.setTitle(`Order Summary - ${this.facilityOrder.facility.name}`);
		this.page = "order-summary";

		const typeResponse = await this.scheduleService.getTypes();
		this.orderType = _.find(typeResponse.item, { abrv: 'ordering' });
	}

	@action.bound
	async setItemValue(value) {
		this.item = value;
		this.itemName = value ? value.itemName : '';
		await this.filterOrderSummaryDetails(value ? value.itemName : '');
	}

	@action.bound
	async hasBackorderedItems() {
		const hasBackorderedItem = (await this.facilityOrderService.hasBackorderedItemNiko(this.facilityOrderId)).data;
		runInAction(() => {
			this.hasBackorderedItem = hasBackorderedItem;
		});
	}

	@action.bound
	async initializeFacilityOrder() {
		const response = await this.facilityOrderService.get(this.facilityOrderId, { embed: "Facility,Facility.FacilityAreaOfCares,FacilityOrderStatuses" });
		const facilityOrder = response.data;
		const isInventoryStepAllowed = (await this.facilityOrderService.isNikoInventoryStepAllowed(this.facilityOrderId)).data;
		runInAction(() => {
			this.facilityOrder = facilityOrder;
			this.isInventoryStepAllowed = isInventoryStepAllowed;
			if (facilityOrder && facilityOrder.facility && facilityOrder.facility.facilityAreaOfCares) {
				this.showRoundOption = !(facilityOrder.facility.facilityAreaOfCares.some(faoc => (faoc.areaOfCare && faoc.areaOfCare.abrv === "enteral") && (faoc.coverageType && faoc.coverageType.abrv === "coveredOnly")));
				this.showFacilityStock = facilityOrder.facility.facilityAreaOfCares.some(faoc => faoc.coverageType && faoc.coverageType.abrv === "all");
			}
			this.disabledSendOrder = !this.isMasterEditor && facilityOrder && facilityOrder.sendOrder;
			this.clinicalReviewStatus = facilityOrder.facilityOrderStatuses.find(x => x.status && x.status.abrv === "clinical-review");
			this.isEditDisabled = facilityOrder && facilityOrder.sendOrder;
			this.shouldShowItemActionButtons = !this.isEditDisabled;
		});
	}

	@action.bound
	async fetchFacilityOrderTemplateItems(filter) {
		this.fetchingData = !this.signalRFetch ? true : false;
		this.signalRFetch = false;

		let params = {
			...filter,
			pageSize: 10000,
			facilityOrderId: this.facilityOrderId,
		}
		if (this.facilityOrder.isEnteralWeek) {
			params = _.extend(params, {
				itemCategoryId: this.enteralItemCategoryId
			});
		}
		let facilityOrderTemplateItemsGroupedByItemCategoryTemp = [];
		const response = await this.nikoPatientTemplateItemService.getNikoOrderSummaryItems(params);
		this.originalData = response && response.data && response.data.item && response.data.item;
		if (this.originalData) {
			let grouped = _.groupBy(this.originalData, 'itemCategory');
			_.each(grouped, (val, key) => {
				facilityOrderTemplateItemsGroupedByItemCategoryTemp.push({
					id: key,
					category: val[0].itemCategory,
					items: _.map(val, (i) => {
						if (i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isDns(x) || this.isNonScheduled(x))) {
							this.anyDNS = true;
						}
						i.facilityOrderTemplateItemOrderSummaryDetails.forEach(d => {
							d.history = { userName: '', eventDate: null, fieldName: '' };
						});
						const computedSSQtySum = _.sumBy(
							i.facilityOrderTemplateItemOrderSummaryDetails.filter(x => x.isEligibleToSend !== false && x.activeStatus === "Active"),
							x => parseInt(x.sendSeparatelyQty ? x.sendSeparatelyQty : 0)
						);
						i.sendSeparatelyQuantitySum = computedSSQtySum;
						i.originalSendSeparatelyQuantitySum = computedSSQtySum;
						return {
							...i,
							showDNSItems: false,
							show: false,
							hasDNSItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isDns(x)),
							isEditMode: false,
							isEditSendSeparatelyMode: false,
							showNonScheduledItems: false,
							isRefreshed: false,
							hasNonScheduledItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isNonScheduled(x)),
							showRoundOption: val[0].itemCategory.toLowerCase() == "enteral" && i.hasCaseVersion && i.qtyPerCase != 0 && i.procedureCode != "B4153" && !i.orderQuantityUnitOfMeasures.includes(", 0 EA") && i.orderQuantitySum != 0,
							includeItem: false
						};
					}),
					show: true
				});
			});

			this.filterItemStore();
			await this.localPatientStore.filterAsync();
			this.facilityOrderTemplateItemsGroupedByItemCategory = facilityOrderTemplateItemsGroupedByItemCategoryTemp;
			this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode = _.cloneDeep(facilityOrderTemplateItemsGroupedByItemCategoryTemp);
			this.fetchingData = false;

			if (this.patientName) {
				this.onPatientChange(this.patientName)
			}
			if (this.itemName) {
				this.onItemChange(this.itemName)
			}
		}
	}

	@action.bound
	changeStatusBarVisibility() {
		this.isStatusBarVisible = !this.isStatusBarVisible;
	}

	@action.bound
	async signalRAction(message) {
		if (!this.facilityOrder.sendOrder) {
			this.hasBackorderedItems();
		}
		if (message.table == "Product") {
			return;
		}
		if (message.table == "FO") {
			if (message.column == "SendOrder") {
				this.facilityOrder.sendOrder = true;
				this.disabledSendOrder = !this.isAdmin;
				this.isEditDisabled = true;
				this.shouldShowItemActionButtons = !this.isEditDisabled
			}
			else {
				await this.getDataAfterImport();
			}
			return;
		}
		else if (message.table == "FOS") {
			return await this.getFacilityOrderStatus();
		}

		await this.refreshData(message);

		if (this.patientFilterValue)
			this.onPatientChange(this.patientFilterValue);
	}

	@action.bound
	async getDataAfterImport() {
		await this.initializeFacilityOrder();
		await this.refreshData();
	}

	@action.bound
	async getFacilityOrderStatus() {
		const facilityOrderStatusService = new FacilityOrderStatusService(this.rootStore.application.baasic.apiClient);
		var statusesResponse = await facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
		this.facilityOrder.facilityOrderStatuses = statusesResponse.data ? [...statusesResponse.data.item] : [];
		this.changeStatusBarVisibility();
		this.changeStatusBarVisibility();
	}

	async fetchData() {
		await this.initialize();
		this.rootStore.notificationStore.warning("This page has been refreshed due to changes made on another page");
	}

	@action.bound
	isDns(x) {
		return (x.isUserDns || x.isSystemDns) && x.activeStatus === "Active";
	}

	@action.bound
	isNonScheduled(x) {
		return x.isEligibleToSend === false && x.activeStatus === "Active" && !x.isUserDns && !x.isSystemDns && x.orderQuantity > 0;
	}

	@action.bound
	refreshEditModeItems(index, index2) {
		if (index != null && index2 != null) {
			this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails
				.forEach(item => {
					item.orderQuantity = this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId == item.facilityOrderTemplateItemId).orderQuantity;
					item.sendSeparatelyQty = this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId == item.facilityOrderTemplateItemId).sendSeparatelyQty;
				});

			this.refreshErrorMessage(this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2]);
		}
	}

	@action.bound
	async refreshErrorMessage(item) {
		if (item) {
			let params = {
				pageSize: 1,
				facilityOrderId: this.facilityOrderId,
				productId: item.productId
			}

			if (this.facilityOrder.isEnteralWeek) {
				params = _.extend(params, {
					itemCategoryId: this.enteralItemCategoryId
				});
			}

			const response = await this.nikoPatientTemplateItemService.getNikoOrderSummaryItems(params);
			item.errorMessage = response && response.data && response.data.item && response.data.item[0] ? response.data.item[0].errorMessage : null;
		}
	}

	@action.bound
	showFacilityOrderTemplateItemOrderSummary(index) {
		this.facilityOrderTemplateItemsGroupedByItemCategory[index].show = !this.facilityOrderTemplateItemsGroupedByItemCategory[index].show;
	}

	@action.bound
	showFacilityOrderTemplateItemOrderSummaryDetails(index, index2) {
		this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].show = !this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].show;
		if (!this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].show) {
			this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].isRefreshed = false;
		}
	}

	@action.bound
	expandAllItemDetails() {
		this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
			category.show = true;
			category.items.forEach(item => {
				item.show = true;
			});
		});
	}

	@action.bound
	collapseAllItemDetails() {
		this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
			category.items.forEach(item => {
				item.show = false;
			});
		});
	}

	@action.bound
	showDNSItems(index, index2) {
		this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showDNSItems = !this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showDNSItems;
	}
	@action.bound
	showNonScheduledItems(index, index2) {
		this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showNonScheduledItems = !this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showNonScheduledItems;
	}

	@action.bound
	showAllDNSItems() {
		this.showAllDNS = !this.showAllDNS;

		this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
			category.items.forEach(item => {
				item.showDNSItems = this.showAllDNS;
				item.showNonScheduledItems = this.showAllDNS;
			});
		});
	}

	@action.bound
	onPatientChange(value) {
		this.patientFilterValue = value;
		this.showAllDNS = !!value;

		this.patientName = value;

		this.filterOrderSummaryDetails(value);

		this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
			category.items.forEach(item => {
				item.showDNSItems = this.showAllDNS;
				item.showNonScheduledItems = this.showAllDNS;
			});
		});
	}

	@action.bound
	onItemChange(value) {
		this.itemName = value;
		this.filterOrderSummaryDetails(value);
	}

	@action.bound
	async filterOrderSummaryDetails(value) {
		const patient = this.localPatientStore.items.find(x => x.name === this.patientName);
		const item = this.itemStore.find(x => x.itemId === this.itemName || x.itemName === this.itemName);
		let facilityOrderTemplateItemsGroupedByItemCategoryTemp = [];
		if (this.originalData) {
			let filteredItems = this.originalData.filter(i =>
				i.facilityOrderTemplateItemOrderSummaryDetails.some(d => (patient ? d.patientId === patient.patientId : d.patient.toLowerCase().startsWith(this.patientName.toLowerCase()))) &&
				(item ? i.itemId === item.itemId : (i.itemId.toLowerCase().includes(this.itemName.toLowerCase()) || i.itemName.toLowerCase().includes(this.itemName.toLowerCase())))
			);

			let grouped = _.groupBy(filteredItems, 'itemCategory');
			_.each(grouped, (val, key) => {
				facilityOrderTemplateItemsGroupedByItemCategoryTemp.push({
					id: key,
					category: val[0].itemCategory,
					items: _.map(val, (i) => {
						return {
							...i,
							facilityOrderTemplateItemOrderSummaryDetails: i.facilityOrderTemplateItemOrderSummaryDetails.filter(d => (patient ? d.patientId === patient.patientId : d.patient.toLowerCase().startsWith(this.patientName.toLowerCase()))),
							showDNSItems: patient || item ? true : false,
							show: value == "" && !patient && !item ? false : true,
							hasDNSItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isDns(x)),
							isEditMode: false,
							isEditSendSeparatelyMode: false,
							showNonScheduledItems: patient || item ? true : false,
							hasNonScheduledItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isNonScheduled(x)),
							showRoundOption: val[0].itemCategory.toLowerCase() == "enteral" && i.hasCaseVersion && i.qtyPerCase != 0 && i.procedureCode != "B4153" && !i.orderQuantityUnitOfMeasures.includes("0 EA") && i.orderQuantitySum != 0,
						};
					}),
					show: true
				});
			});
		}
		this.facilityOrderTemplateItemsGroupedByItemCategory = facilityOrderTemplateItemsGroupedByItemCategoryTemp
		this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode = _.cloneDeep(facilityOrderTemplateItemsGroupedByItemCategoryTemp);

		this.rebuildTooltip = true;
		this.rebuildTooltip = false;

	}

	@action.bound
	async cutbackSalesOrderQuantity(option, group, index, index2) {
		if (option == "cutback-all" && group.defaultItemTypeAbrv == "spike-set" && group.facilityOrderTemplateItemOrderSummaryDetails.some(x => (x.clinicalStatus == "Covered" || x.clinicalStatus == "Prior Auth Obtained") && x.orderQuantity > 0 && !(x.isUserDns || x.isSystemDns))) {
			this.rootStore.notificationStore.error("Unable to cutback on all for spike sets. (Cutting back on covered spike sets may cause billing issues. to adjust, use \u201cCut Back on Specific\u201d instead.)");
			return;
		}

		if (option == "cutback-noncovered") {
			await this.cutbackSOQuantities(option, group);
			this.refreshEditModeItems(index, index2);
		}
		else if (option == "cutback-all") {
			this.cutbackConfirmModal.onConfirm = async () => { await this.cutbackSOQuantities(option, group, index); this.cutbackConfirmModal.close(); };
			this.cutbackConfirmModal.open({
				message: "Are you sure you want to cutback on the Covered patients?"
			});
		}
		else if (option == "cutback-specific") {
			group.isEditMode = true;
			group.isEditSendSeparatelyMode = false;
		}
	}

	@action.bound
	async cutbackSOQuantities(option, group, index, index2) {
		this.savingResource = true;
		if (index != null && index2 != null &&
			this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.some(x => {
				const original = this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(
					i => i.facilityOrderTemplateItemId === x.facilityOrderTemplateItemId
				);
				return (group.defaultItemTypeAbrv === "spike-set") && (x.clinicalStatus === "Covered" || x.clinicalStatus === "Prior Auth Obtained") && original && x.orderQuantity != original.orderQuantity;
			})
		) {
			this.rootStore.notificationStore.warning("When cutting back on covered spike sets, the syringes cannot be covered. Please adjust accordingly.");
		}

		let facilityOrderTemplateIds = null;
		if (index != null && index2 != null) {
			facilityOrderTemplateIds = this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.map(x => ({ id: x.facilityOrderTemplateItemId, orderQuantity: x.orderQuantity }));
		}
		else {
			facilityOrderTemplateIds = group.facilityOrderTemplateItemOrderSummaryDetails.filter(x => (x.activeStatus === "Active" && !(x.isUserDns || x.isSystemDns)) && (option == "cutback-noncovered" ? !(x.eligibilityStatus == "Eligible" && (x.clinicalStatus == "Covered" || x.clinicalStatus == "Prior Auth Obtained" || x.clinicalStatus == "Prior Auth Required" || x.clinicalStatus == "Prior Auth Pending" || x.clinicalStatus == "Does Not Meet Insurance Criteria" || x.clinicalStatus == null)) : true) && (group.defaultItemTypeAbrv != "spike-set" || (group.defaultItemTypeAbrv == "spike-set" && (x.clinicalStatus != "Covered" && x.clinicalStatus != "Prior Outh Obtained")))).map(x => ({ id: x.facilityOrderTemplateItemId, orderQuantity: null }));
		}

		if (facilityOrderTemplateIds.some(x => x)) {
			let result = await this.nikoPatientTemplateItemService.nikoCutbackSalesOrderQuantities(facilityOrderTemplateIds);
			if (result.statusCode == 201 && result.data && result.data.length > 0) {
				let isUpdated = false;
				result.data.forEach(item => {
					let matchingItem = group.facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId === item.id);
					if (matchingItem) {
						if (!isUpdated)
							isUpdated = matchingItem.orderQuantity != item.orderQuantity;
						matchingItem.orderQuantity = !item.orderQuantity ? 0 : item.orderQuantity;
						if (index != null && index2 != null) {
							this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId === item.id).orderQuantity = item.orderQuantity;
						}
						else if (index != null) {
							this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items.forEach(i => {
								let matchingItem = i.facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId === item.id);
								if (matchingItem) {
									if (!isUpdated)
										isUpdated = matchingItem.orderQuantity != item.orderQuantity;
									matchingItem.orderQuantity = item.orderQuantity
								}
							});
						}
					}
				});
				this.recalculateOrderQuantity(group);
				if (index != null && index2 != null) {
					this.refreshErrorMessage(this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2]);
				}
				this.rebuildTooltip = true;
				this.rebuildTooltip = false;
				if (isUpdated)
					this.rootStore.notificationStore.success("SO quantities successfully updated.", 2000);
				group.originalSendSeparatelyQuantitySum = group.sendSeparatelyQuantitySum;
				this.rebuildTooltip = true;
				this.rebuildTooltip = false;
				this.syncQuantityToOriginalData(result.data, "orderQuantity");
			} else {
				this.rootStore.notificationStore.error("An error occured while updating SO quantities.");
			}
		}
		else {
			this.rootStore.notificationStore.warning("No items found for update.");
		}
		this.hasBackorderedItems();
		group.isEditMode = false;
		this.savingResource = false;
	}

	@action.bound
	async cutbackSendSeparatelyQuantities(group, index, index2, inputValue) {
		this.savingResource = true;

		const ssQty = parseInt(inputValue);

		const items = this
			.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index]
			.items[index2]
			.facilityOrderTemplateItemOrderSummaryDetails
			.map(x => ({ id: x.facilityOrderTemplateItemId }));

		const result = await this.nikoPatientTemplateItemService
			.nikoUpdateBatchSendSeparatelyQuantity({ ssQty, items });

		if (result.statusCode == 201) {
			let isUpdated = false;
			// Update endpoint returns sendSeparatelyQuantity (PatientTemplateItem field name),
			// but order summary details use sendSeparatelyQty — map to match
			const mappedData = result.data.map(item => ({ ...item, sendSeparatelyQty: item.sendSeparatelyQuantity }));
			mappedData.forEach(item => {
				let matchingItem = group.facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId === item.id);
				if (matchingItem) {
					if (!isUpdated) {
						isUpdated = matchingItem.sendSeparatelyQty != item.sendSeparatelyQty;
					}
					matchingItem.sendSeparatelyQty = item.sendSeparatelyQty;
				}
			});
			group.sendSeparatelyQuantitySum = _.sumBy(
				group.facilityOrderTemplateItemOrderSummaryDetails.filter(
					i => i.isEligibleToSend !== false && i.activeStatus === "Active"
				),
				i => parseInt(i.sendSeparatelyQty ? i.sendSeparatelyQty : 0)
			);
			this.rebuildTooltip = true;
			this.rebuildTooltip = false;
			if (isUpdated)
				this.rootStore.notificationStore.success("SS QTY successfully added.", 2000);
			group.originalSendSeparatelyQuantitySum = ssQty;
			this.rebuildTooltip = true;
			this.rebuildTooltip = false;
			this.syncQuantityToOriginalData(mappedData, "sendSeparatelyQty");
		} else {
			this.rootStore.notificationStore.error("An error occured while updating SO quantities.");
		}
		this.refreshEditModeItems(index, index2);
		group.isEditSendSeparatelyMode = false;
		this.savingResource = false;
	}

	@action.bound
	resetSSQuantitySum(group) {
		group.isDefaultUpdated = false;
		group.sendSeparatelyQuantitySum = _.sumBy(
			group.facilityOrderTemplateItemOrderSummaryDetails.filter(
				i => i.isEligibleToSend !== false && i.activeStatus === "Active"
			),
			i => parseInt(i.sendSeparatelyQty ? i.sendSeparatelyQty : 0)
		);
	}

	@action.bound
	syncQuantityToOriginalData(updated, valueField) {
		if (!this.originalData || !this.originalData.length || !updated || !updated.length) return;

		const valueById = {};
		for (const item of updated) {
			valueById[item.id] = item[valueField] != null ? item[valueField] : 0;
		}

		for (const group of this.originalData) {
			const details = group.facilityOrderTemplateItemOrderSummaryDetails || [];
			let wasModified = false;

			for (const detail of details) {
				if (valueById.hasOwnProperty(detail.facilityOrderTemplateItemId)) {
					detail[valueField] = valueById[detail.facilityOrderTemplateItemId];
					wasModified = true;
				}
			}

			if (!wasModified) continue;

			if (valueField === "orderQuantity") {
				const activeScheduledItems = details.filter(item => {
					const isActive = item.activeStatus === "Active" && !(item.isUserDns || item.isSystemDns);
					const isScheduled = item.isEligibleToSend !== false || !(parseInt(item.orderQuantity, 10) > 0);
					return isActive && isScheduled;
				});

				const sum = _.sumBy(activeScheduledItems, item => parseInt(item.orderQuantity, 10) || 0);

				group.orderQuantitySum = sum;

				if (!group.qtyPerCase || sum === 0) {
					group.orderQuantityUnitOfMeasures = "";
				} else {
					const cs = Math.floor(sum / group.qtyPerCase);
					const ea = sum % group.qtyPerCase;
					group.orderQuantityUnitOfMeasures = `${cs} CS, ${ea} EA`;
				}
			} else if (valueField === "sendSeparatelyQty") {
				group.sendSeparatelyQuantitySum = _.sumBy(
					group.facilityOrderTemplateItemOrderSummaryDetails.filter(
						i => i.isEligibleToSend !== false && i.activeStatus === "Active"
					),
					i => parseInt(i.sendSeparatelyQty ? i.sendSeparatelyQty : 0)
				);
			}
		}
	}

	@action.bound
	editOrderQuantity(item, index, index2, value) {
		this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId == item.facilityOrderTemplateItemId).orderQuantity = parseInt(value);
	}

	@action.bound
	saveOrderQuantity(item, index, index2, value) {
		let minQuantity = item.sendSeparatelyQty ? item.orderQuantity - (item.orderQuantity - item.sendSeparatelyQty) : 0;
		if (parseInt(value) < minQuantity) {
			this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId == item.facilityOrderTemplateItemId).orderQuantity = "";
			this.rootStore.notificationStore.error("An SS has already been applied to this item. The sales order qty cannot be lower than the SS qty.");
		}
	}

	@action.bound
	editBatchSendSeparatelyQty(group, index, index2, value) {
		if (parseInt(value) < 0) {
			this.rootStore.notificationStore.error("SS QTY must be greater than 0.");
			return;
		}
		let groupTotalSO = group.orderQuantitySum ? parseInt(group.orderQuantitySum, 10) : 0;
		if (parseInt(value) > groupTotalSO) {
			this.rootStore.notificationStore.error("SS QTY mustn't be greater than SO QTY sum.");

			if (this.ssQtyInputRef) {
				this.ssQtyInputRef.value = "";
			}
			return;
		}
		group.sendSeparatelyQuantitySum = value;
		group.isDefaultUpdated = true;
	}

	@action.bound
	showItem(index, index2) {
		return this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.some(x => x.activeStatus === "Active" && !this.isDns(x) && !this.isNonScheduled(x));
	}

	@action.bound
	orderHistory() {
		window.open(this.rootStore.routerStore.findRoute("master.app.main.niko-facility-order.history").pattern.replace(":id", this.facilityOrder.id));
	}

	@action.bound
	async goToFacilityPreview() {
		window.open(`${this.rootStore.routerStore.findRoute('master.app.main.facility.preview').pattern.replace(":id", this.facilityOrder.facilityId)}`);
	}

	@action.bound
	async goToFacilityCalendar() {
		window.open(`${this.rootStore.routerStore.findRoute('master.app.main.schedule.calendar').pattern}?facilityId=${this.facilityOrder.facilityId}&calendarTypeId=${this.orderType.id}`);
	}

	@action.bound
	goToPatientTemplates() {
		const dos = this.facilityOrder.dateOfService ? new Date(this.facilityOrder.dateOfService).toISOString() : '';
		window.open(`${this.rootStore.routerStore.findRoute('master.app.main.sales-order.niko-sales-order').pattern}?facilityId=${this.facilityOrder.facilityId}&dosDateFrom=${dos}&dosDateTo=${dos}`);
	}

	@action.bound
	async openInsuranceCoverageStatusOverviewEmail() {
		window.open(`${this.rootStore.routerStore
			.findRoute('master.app.main.niko-facility-order.niko-overview.new-flow-edit-email')
			.pattern.replace(":facilityOrderId", this.facilityOrder.id)}?isNiko=true`);
	}

	@action.bound
	async showSendOrderConfirmModal() {
		try {
			this.isSendOrderChecking = true;
			let res = (await this.facilityOrderService.importCheckNiko(this.facilityOrder.id)).data;
			if (!res.success) {
				return this.rootStore.notificationStore.error(res.errors);
			}

			const recipientData = await this.hasPrimaryRecipient();
			const hasPickTicketRecipient = recipientData.pickTicketRecipientType;
			const hasPrimaryDeliveryRecipient = recipientData.deliveryTicketType;

			if (!hasPickTicketRecipient || !hasPrimaryDeliveryRecipient) {
				const missingName = [!hasPickTicketRecipient && "Packing List", !hasPrimaryDeliveryRecipient && "Delivery Ticket"].filter(Boolean).join(" and ");
				return this.openPrimaryRecipientErrorModal(this.showSendOrderConfirmModalExtension, missingName);
			}
			await this.showSendOrderConfirmModalExtension();
		} catch (err) {
			this.rootStore.notificationStore.error("Error occurred while validating send order.", err);
		}
		finally {
			this.isSendOrderChecking = false;
		}
	}

	@action.bound
	async showSendOrderConfirmModalExtension() {
		await this.sendOrderConfirmModal.open({
			message: 'FACILITY_ORDER_TEMPLATE.EDIT.CONFIRM_SEND_ORDER'
		});
	}

	@action.bound
	async hasPrimaryRecipient() {
		return (await this.facilityOrderService.hasPrimaryRecipientNiko(this.facilityOrderId)).data;
	}

	@action.bound
	openPrimaryRecipientErrorModal(processAction, missingName) {
		this.primaryRecipientErrorModal.open({
			processAction: processAction,
			missingName: missingName
		});
	}

	@action.bound
	async sendOrder() {
		try {
			this.disabledSendOrder = true;
			this.sendOrderAll = true;
			const response = await this.facilityOrderService.sendOrder({ id: this.facilityOrderId });
			if (response && response.data && response.data.customValues && response.data.customValues.message) {
				this.rootStore.notificationStore.success(response.data.customValues.message);
			}
			// ISG-871: surface drainage bag / leg bag validation changes as orange warning toasts.
			const validationMessages = response && response.data && response.data.customValues && response.data.customValues.validationMessages;
			if (validationMessages && validationMessages.length) {
				validationMessages.forEach((message) => this.rootStore.notificationStore.warning(message));
			}
		}
		catch (err) {
			this.rootStore.notificationStore.error("Error occurred while trying to send order.");
		}
		finally {
			await this.initializeFacilityOrder();
			await this.queryUtility.fetch();
			this.disabledSendOrder = false;
			this.sendOrderAll = false;
		}
	}

	@action.bound
	isNewPatientById(patientId) {
		const items = this.localPatientStore && this.localPatientStore.items || [];
		return items.some(p => p && p.patientId === patientId && p.isNewPatient);
	}

	@action.bound
	async openFacilityOrderNewFlowUSIntakeFindAndReplaceModal() {
		if (this.disabled) {
			this.rootStore.notificationStore.error("This action is not allowed until the Clinical Review is started.");
			return;
		}

		this.facilityOrderNewFlowUSIntakeFindAndReplaceModal.open({
			facilityOrderId: this.facilityOrder.id,
			fetchData: async () => {
				await this.refreshData();
				if (this.patientFilterValue) this.onPatientChange(this.patientFilterValue);
			}
		});
	}

	@action.bound
	openFacilityOrderNewFlowExtraSuppliesAddModal() {
		this.facilityOrderNewFlowExtraSuppliesAddModal.open({
			facilityOrderId: this.facilityOrder.id,
			facilityId: this.facilityOrder.facilityId,
			sendAgoraPo: this.facilityOrder.facility.sendAgoraPo,
			skipAutoItems: true,
			// fetchData: async () => {
			// 	await this.refreshData();
			// 	if (this.patientFilterValue) this.onPatientChange(this.patientFilterValue);
			// }
		});
	}

	@action.bound
	async openRoundItemsModal(group) {
		await this.initializeFacilityOrder();
		let inventoryStatus = this.facilityOrder.facilityOrderStatuses.find(x => x.status && x.status.abrv === "inventory");
		if (this.isInventoryStepAllowed && (!(this.facilityOrder.missingInventoryCount || (inventoryStatus && inventoryStatus.completedOn)))) {
			this.rootStore.notificationStore.error("Rounding can only be done once the inventory is completed or skipped.");
			this.isEditDisabled = this.facilityOrder && this.facilityOrder.sendOrder;
			this.savingResource = false;
			this.disabledItemButtons = false;
			return;
		}

		let isRoundDown = group ? this.isRoundDown(group.orderQuantityUnitOfMeasures, group.qtyPerCase) : null;

		if (group && isRoundDown && group.facilityOrderTemplateItemOrderSummaryDetails && !group.facilityOrderTemplateItemOrderSummaryDetails.some(x => !(x.eligibilityStatus == "Eligible" && (x.clinicalStatus == "Prior Auth Obtained" || x.clinicalStatus == "Prior Auth Required" || x.clinicalStatus == "Prior Auth Pending" || x.clinicalStatus == "Covered" || x.clinicalStatus == "Does Not Meet Insurance Criteria" || x.clinicalStatus == null)) && x.orderQuantity > 0 && x.orderQuantity >= x.sendSeparatelyQty)) {
			this.rootStore.notificationStore.error("Rounding down is not allowed because cutbacks cannot be applied to eligible patients.");
			this.isEditDisabled = this.facilityOrder && this.facilityOrder.sendOrder;
			this.savingResource = false;
			this.disabledItemButtons = false;
			return;
		}

		let items = [];
		if (!group) {
			this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
				category.items.forEach(item => {
					if (item.errorMessage != "Rounding is not allowed for this item because it can only be partially rounded." && this.isRoundDown(item.orderQuantityUnitOfMeasures, item.qtyPerCase) && item.showRoundOption && item.patientCount < 4 && item.facilityOrderTemplateItemOrderSummaryDetails.some(x => !(x.eligibilityStatus == "Eligible" && (x.clinicalStatus == "Prior Auth Obtained" || x.clinicalStatus == "Prior Auth Required" || x.clinicalStatus == "Prior Auth Pending" || x.clinicalStatus == "Covered" || x.clinicalStatus == "Does Not Meet Insurance Criteria" || x.clinicalStatus == null)) && x.orderQuantity > 0 && x.orderQuantity >= x.sendSeparatelyQty)) {
						items.push(item);
					}
				});
			});
		}

		if (group && group.errorMessage != "Rounding is not allowed for this item because it can only be partially rounded." && isRoundDown && group.patientCount < 4) {
			this.facilityOrderNewFlowRoundingItemsModal.open({
				roundItems: async () => await this.roundToNearestCase(group)
			});
		}
		else if (items && items.length > 0) {
			this.facilityOrderNewFlowRoundingItemsModal.open({
				roundItems: async () => await this.roundToNearestCase(group),
				items: items
			});
		}
		else if (group && group.errorMessage == "Rounding is not allowed for this item because it can only be partially rounded." && isRoundDown) {
			this.rootStore.notificationStore.error("Rounding is not allowed for this item because it can only be partially rounded.");
			this.isEditDisabled = this.facilityOrder && this.facilityOrder.sendOrder;
			this.savingResource = false;
			this.disabledItemButtons = false;
			return;
		}
		else {
			await this.roundToNearestCase(group);
		}
	}

	isRoundDown(orderQuantityString, qty) {
		const parts = orderQuantityString.split(",");
		if (parts.length < 2)
			return false;

		const secondPart = parts[1].trim();
		const match = secondPart.match(/^(\d+)/);

		if (!match)
			return false;

		const secondNumber = parseInt(match[1], 10);

		return secondNumber < qty / 2;
	}

	@action.bound
	async roundToNearestCase(group) {
		this.savingResource = true;
		this.isEditDisabled = true;
		this.disabledItemButtons = true;
		try {
			var response = await this.nikoPatientTemplateItemService.nikoRoundToTheNearestCase({ facilityOrderId: this.facilityOrderId, productId: group ? group.productId : null, facilityOrderTemplateItemIds: group ? [group.productId] : _.flatMap(this.facilityOrderTemplateItemsGroupedByItemCategory, category => _.filter(category.items, item => item.includeItem)).map(x => x.productId) });
			if (response && response.data && response.data.errors && response.data.errors[0]) {
				this.rootStore.notificationStore.error(response.data.errors[0]);
				this.isEditDisabled = this.facilityOrder && this.facilityOrder.sendOrder;
				this.savingResource = false;
				this.disabledItemButtons = false;
				return;
			}
			else {
				this.rootStore.notificationStore.success(group ? "Item successfully rounded." : "All items that were able to be rounded up or down to the nearest case have been adjusted.");
			}
		}
		catch (err) {
			this.rootStore.notificationStore.error("An error occured while rounding.");
		}

		this.isEditDisabled = this.facilityOrder && this.facilityOrder.sendOrder;
		this.savingResource = false;
		this.disabledItemButtons = false;
		await this.refreshData();

		if (this.patientFilterValue)
			this.onPatientChange(this.patientFilterValue);

	}

	@action.bound
	recalculateOrderQuantity(group) {
		let filteredItems = group.facilityOrderTemplateItemOrderSummaryDetails.filter(x => x.activeStatus === "Active" && !(x.isUserDns || x.isSystemDns) && x.isEligibleToSend !== false);
		let SOQtySum = _.sumBy(filteredItems, i => parseInt(i.orderQuantity ? i.orderQuantity : 0));
		let orderQuantityUnitOfMeasures = group.qtyPerCase == 0 ? `0 CS, ${SOQtySum} EA` : `${Math.floor(SOQtySum / group.qtyPerCase)} CS, ${SOQtySum % group.qtyPerCase} EA`;
		group.orderQuantityUnitOfMeasures = group.qtyPerCase == 0 || group.qtyPerCase == null || orderQuantityUnitOfMeasures == 0 ? "" : orderQuantityUnitOfMeasures;
		group.orderQuantitySum = SOQtySum;
		group.showRoundOption = group.itemCategory.toLowerCase() == "enteral" && group.hasCaseVersion && group.qtyPerCase != 0 && group.procedureCode != "B4153" && !group.orderQuantityUnitOfMeasures.includes(", 0 EA") && group.orderQuantitySum != 0;
	}

	@action.bound
	setSsQtyInputRef(el) {
		this.ssQtyInputRef = el;
	}

	@action.bound
	getSsQtyInputValue() {
		return this.ssQtyInputRef ? this.ssQtyInputRef.value : '';
	}

	@action.bound
	async refreshData(message) {
		this.setAnyDNS();

		let params = {
			...this.queryUtility.plainFilter,
			pageSize: 10000,
			facilityOrderId: this.facilityOrderId,
		};

		if (this.facilityOrder.isEnteralWeek) {
			params = _.extend(params, {
				itemCategoryId: this.enteralItemCategoryId
			});
		}

		const response = await this.nikoPatientTemplateItemService.getNikoOrderSummaryItems(params);
		this.originalData = (response && response.data && response.data.item) ? response.data.item : null;

		if (!this.originalData) {
			return;
		}

		let foundDNS = false;
		let facilityOrderTemplateItemsGroupedByItemCategoryTemp = [];

		const patient = this.localPatientStore.items.find(x => x.name === this.patientName);
		const item = this.itemStore.find(x => x.itemId === this.itemName || x.itemName === this.itemName);
		let filteredItems = this.originalData.filter(i =>
			i.facilityOrderTemplateItemOrderSummaryDetails.some(d => (patient ? d.patientId === patient.patientId : d.patient.toLowerCase().startsWith(this.patientName.toLowerCase()))) &&
			(item ? i.itemId === item.itemId : (i.itemId.toLowerCase().includes(this.itemName.toLowerCase()) || i.itemName.toLowerCase().includes(this.itemName.toLowerCase()))));

		let grouped = _.groupBy(filteredItems, 'itemCategory');

		_.each(grouped, (val, key) => {
			facilityOrderTemplateItemsGroupedByItemCategoryTemp.push({
				id: key,
				category: val[0].itemCategory,
				items: _.map(val, (i) => {
					if (i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isDns(x) || this.isNonScheduled(x))) {
						foundDNS = true;
					}
					i.facilityOrderTemplateItemOrderSummaryDetails.forEach(d => {
						d.history = { userName: '', eventDate: null, fieldName: '' };
					});

					let currentItem = this.facilityOrderTemplateItemsGroupedByItemCategory.find(x => x.id == key) && this.facilityOrderTemplateItemsGroupedByItemCategory.find(x => x.id == key).items.find(x => x.itemId == i.itemId);

					const locallyChanged =
						currentItem &&
						(
							i.orderQuantitySum !== currentItem.orderQuantitySum ||
							i.sendSeparatelyQuantitySum !== currentItem.sendSeparatelyQuantitySum ||
							i.facilityOrderTemplateItemOrderSummaryDetails.length != currentItem.facilityOrderTemplateItemOrderSummaryDetails.length
						);
					let refreshed = !currentItem || (currentItem && message && message.table == "FOT" && i.facilityOrderTemplateItemOrderSummaryDetails && i.facilityOrderTemplateItemOrderSummaryDetails.length != currentItem.facilityOrderTemplateItemOrderSummaryDetails.length) || (message && i.facilityOrderTemplateItemOrderSummaryDetails && i.facilityOrderTemplateItemOrderSummaryDetails.some(x => message.table == "FOTI" ? (x.facilityOrderTemplateItemId == message.customId || ((i.facilityOrderTemplateItemOrderSummaryDetails && i.facilityOrderTemplateItemOrderSummaryDetails.length != currentItem.facilityOrderTemplateItemOrderSummaryDetails.length) && currentItem.itemId == i.itemId)) : x.facilityOrderPatientId == message.id || x.facilityOrderTemplateId == message.id)) || (!message && locallyChanged);
					const computedSSQtySum = _.sumBy(
						i.facilityOrderTemplateItemOrderSummaryDetails.filter(x => x.isEligibleToSend !== false && x.activeStatus === "Active"),
						x => parseInt(x.sendSeparatelyQty ? x.sendSeparatelyQty : 0)
					);
					i.sendSeparatelyQuantitySum = computedSSQtySum;
					i.originalSendSeparatelyQuantitySum = computedSSQtySum;
					return {
						...i,
						isRefreshed: (currentItem && (currentItem.isEditMode || currentItem.isEditSendSeparatelyMode) && refreshed) || (currentItem && currentItem.isRefreshed),
						showDNSItems: currentItem && currentItem.showDNSItems,
						show: !(currentItem && (currentItem.isEditMode || currentItem.isEditSendSeparatelyMode) && refreshed) && currentItem && currentItem.show,
						hasDNSItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isDns(x)),
						isEditMode: !refreshed && currentItem && currentItem.isEditMode,
						isEditSendSeparatelyMode: currentItem && currentItem.isEditSendSeparatelyMode && !refreshed,
						showNonScheduledItems: currentItem && currentItem.showNonScheduledItems,
						hasNonScheduledItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isNonScheduled(x)),
						showRoundOption:
							val[0].itemCategory.toLowerCase() === "enteral" &&
							i.hasCaseVersion &&
							i.qtyPerCase !== 0 &&
							i.procedureCode !== "B4153" &&
							!i.orderQuantityUnitOfMeasures.includes(", 0 EA") &&
							i.orderQuantitySum !== 0,
						includeItem: false
					};
				}),
				show: this.facilityOrderTemplateItemsGroupedByItemCategory.find(x => x.id == key) && this.facilityOrderTemplateItemsGroupedByItemCategory.find(x => x.id == key).show
			});
		});
		if (foundDNS) {
			this.setAnyDNS.cancel();
			this.anyDNS = true;
		}

		this.filterItemStore();
		await this.localPatientStore.filterAsync();

		this.facilityOrderTemplateItemsGroupedByItemCategory = facilityOrderTemplateItemsGroupedByItemCategoryTemp;
		this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode = facilityOrderTemplateItemsGroupedByItemCategoryTemp;

		await this.hasBackorderedItems();

		this.rebuildTooltip = true;
		this.rebuildTooltip = false;
	}

	@action.bound
	filterItemStore() {
		this.itemStore = this.originalData.map(i => ({
			itemId: i.itemId,
			itemName: i.itemName,
			description: i.itemName
		}));
	}

	@action.bound
	async openAddTubeConfirmModal() {
		if (this.facilityOrder.sendOrder) {
			await this.checkForMonthlyTubesNow();
		}
		else {
			this.addTubeConfirmModal.open({
				message: 'The system will not update the "above allowable" template if changes are made to the order.',
				title: 'Add Tubes Now?'
			});
		}
	}

	@action.bound
	async checkForMonthlyTubesNow() {
		try {
			await this.nikoPatientTemplateService.createMonthlyTubeMasterTemplates(this.facilityOrderId);
			this.rootStore.notificationStore.success('The "above allowable" template successfully created.');
		}
		catch (e) {
			this.rootStore.notificationStore.error(getErrorMessage(e, "Error occurred while checking for monthly tubes."));
		}
		finally {
			await this.getDataAfterImport();
		}
	}

	@action.bound
	async handleAdditionalActions(name) {
		if (name === "Add IV Poles") {
			await this.showAddIvPolesConfirmModal();
		} else if (name === "Check for Monthly Tubes Now") {
			await this.openAddTubeConfirmModal();
		}
	}

	@action.bound
	async handleAdminActions(name) {
		if (name === "Send Packing List (this FO)") {
			this.facilityOrderService.sendNikoPackingListEmailSingle(this.facilityOrderId);
		} else if (name === "Send Packing List (all FOs)") {
			this.facilityOrderService.sendNikoPackingListEmail();
		} else if (name === "Send Agora PO") {
			await this.sendAgoraPo();
		} else if (name === "Create Niko SOs (all FOs)") {
			await this.createNikoSOsAllFacilityOrders();
		} else if (name === "Create Niko Invoices (all FOs)") {
			await this.createNikoInvoicesAllFacilityOrders();
		}
	}

	// ISG-2280: manual trigger for the overnight Niko sales order run - queues the job for every eligible facility order.
	@action.bound
	async createNikoSOsAllFacilityOrders() {
		if (this.creatingNikoSOsAllFacilityOrders) return;
		this.creatingNikoSOsAllFacilityOrders = true;
		try {
			await this.facilityOrderService.createNikoSOsAllFacilityOrders();
			this.rootStore.notificationStore.success("Niko Sales Order creation has been queued for all facility orders.");
		}
		catch (err) {
			this.rootStore.notificationStore.error(getErrorMessage(err, "Error occurred while trying to create Niko Sales Orders."));
		}
		finally {
			this.creatingNikoSOsAllFacilityOrders = false;
		}
	}

	// ISG-2281: manual trigger for the overnight Niko invoice run - queues the job for every eligible facility order.
	@action.bound
	async createNikoInvoicesAllFacilityOrders() {
		if (this.creatingNikoInvoicesAllFacilityOrders) return;
		this.creatingNikoInvoicesAllFacilityOrders = true;
		try {
			await this.facilityOrderService.createNikoInvoicesAllFacilityOrders();
			this.rootStore.notificationStore.success("Niko Invoice creation has been queued for all facility orders.");
		}
		catch (err) {
			this.rootStore.notificationStore.error(getErrorMessage(err, "Error occurred while trying to create Niko Invoices."));
		}
		finally {
			this.creatingNikoInvoicesAllFacilityOrders = false;
		}
	}

	@action.bound
	async sendAgoraPo() {
		this.sendingAgoraPo = true;
		try {
			const pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);
			await pickTicketHeaderService.sendAgoraPoNiko(this.facilityOrderId);
			this.rootStore.notificationStore.success("Agora PO successfully sent.");
		}
		catch (err) {
			return err.data.errors == this.agoraPoErrorMessage ? this.rootStore.notificationStore.success(err.data.errors) : this.rootStore.notificationStore.error("Unable to send Agora PO!");
		}
		finally {
			this.sendingAgoraPo = false;
		}
	}

	@action.bound
	async downloadReport(name) {
		const pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);
		const result = await pickTicketHeaderService.hasPickTicketItemsNiko(this.facilityOrderId, name);
		if (!result.hasItems) {
			const messages = {
				"Pick Ticket": "There are no items to include on the Pick Ticket.",
				"Packing List": "There are no items to include on the Packing List.",
				"Delivery Ticket": "There are no items to include on the Delivery Ticket.",
				"Agora PO": "There are no items to include on the Agora PO.",
			};
			this.rootStore.notificationStore.error(messages[name] || "There are no items to include.");
			return;
		}

		if (name === "Pick Ticket") {
			this.downloadPickTicket('XLSX');
		} else if (name === "Packing List") {
			this.downloadPickTicket('PDF');
		} else if (name === "Delivery Ticket") {
			this.downloadDeliveryTicket();
		} else if (name === "Agora PO") {
			this.downloadAgoraPo();
		}
	}

	@action.bound
	async downloadPickTicket(format) {
		const pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);
		const url = `${pickTicketHeaderService.pickTicketReportDownloadUrlNiko(this.facilityOrderId)}?format=${format}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
		window.open(url);
	}

	@action.bound
	async downloadDeliveryTicket() {
		const pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);
		const url = `${pickTicketHeaderService.getDeliveryReportDownloadUrlNiko(this.facilityOrderId)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
		window.open(url);
	}

	@action.bound
	async downloadAgoraPo() {
		const pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);
		const url = `${pickTicketHeaderService.getAgoraPoReportDownloadUrlNiko(this.facilityOrderId)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
		window.open(url);
	}

	@action.bound
	async showAddIvPolesConfirmModal() {
		await this.addIvPolesConfirmModal.open({
			message: 'Adding IV Poles prior to sending order should only be done if fully necessary. Once this check is done, future changes to templates that affect their need for IV Poles will not be accounted for.'
		});
	}

	@action.bound
	async addIvPoles() {
		try {
			const response = await this.facilityOrderService.addIvPoles({ id: this.facilityOrderId });
			if (response && response.data && response.data.customValues && response.data.customValues.message) {
				this.rootStore.notificationStore.success(response.data.customValues.message);
			}
			await this.queryUtility.fetch();
		}
		catch (err) {
			this.rootStore.notificationStore.error("Error occurred while trying to add IV poles.");
		}
	}

	@action.bound
	async getFieldHistory(item, fieldName, value, recordId) {
		if (item.history) {
			const history = (await this.historyService.getRecordHistory({ recordId: recordId || item.facilityOrderTemplateItemId, fieldName: fieldName, value: value })).data;
			item.history.userName = history.userName;
			item.history.eventDate = history.eventDate;
			item.history.fieldName = fieldName;
		}
	}

	@action.bound
	async getItemAddedByHistory(item) {
		if (item.history) {
			const history = (await this.historyService.getItemActionHistory(item.facilityOrderTemplateItemId)).data;
			item.history.userName = history.userName;
			item.history.eventDate = history.eventDate;
			item.history.action = history.action;
			item.history.fieldName = 'ImportAction';
		}
	}

	@action.bound
	async updateBackorderNote(group, value) {
		try {
			await this.facilityOrderProductService.upsert({
				backorderNote: value,
				facilityOrderId: this.facilityOrderId,
				productId: group.productId
			});
			// Mutation after `await` runs outside the action.bound
			// envelope around `updateBackorderNote`; MobX strict mode
			// rejects it as "changing observable values without using
			// an action" and (more importantly) the value-update
			// notification is dropped in some MobX 6 configurations,
			// so observers never see the write. Wrap so the change
			// participates in MobX's batched reaction cycle.
			runInAction(() => {
				group.backorderNote = value;
			});
		} catch (err) {
			this.rootStore.notificationStore.error("Error occurred while updating backorder note.");
		}
	}
}

export default NikoFacilityOrderOrderSummaryPreviewViewStore;
